import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { latestWidgetsApi, sitesApi, statsApi } from '../api/client';

export default function Dashboard() {
  const { data: widgetsData, isLoading: loadingWidgets } = useQuery({
    queryKey: ['latestWidgets'],
    queryFn: () => latestWidgetsApi.list(10),
  });

  const { data: sitesData, isLoading: loadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
  });

  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['stats', 30],
    queryFn: () => statsApi.summary({ days: 30 }),
  });

  const widgets = widgetsData?.widgets || [];
  const sites = sitesData?.sites || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <Link to="/sites" className="btn btn-primary">
          Manage Sites
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Poll Votes"
          value={stats?.total_poll_votes}
          loading={loadingStats}
          period="All time"
        />
        <StatCard
          label="Starts"
          value={stats?.total_starts}
          loading={loadingStats}
        />
        <StatCard
          label="Completions"
          value={stats?.total_completions}
          loading={loadingStats}
          subtitle={stats && stats.total_starts > 0
            ? `${Math.round((stats.total_completions / stats.total_starts) * 100)}% of starts`
            : undefined}
        />
        <StatCard
          label="Shares"
          value={stats?.total_shares}
          loading={loadingStats}
          subtitle={stats && stats.total_completions > 0
            ? `${Math.round((stats.total_shares / stats.total_completions) * 100)}% of completions`
            : undefined}
        />
      </div>

      {/* Latest Widgets */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Latest Widgets</h2>
          <span className="text-sm text-gray-500">{widgets.length} recent</span>
        </div>
        {loadingWidgets ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : widgets.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No widgets created yet</p>
            <p className="text-sm text-gray-400">
              Widgets are automatically generated when posts are published on your connected sites.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {widgets.map((widget) => {
              const content = JSON.parse(widget.content);
              const title = content.hook_title || content.question || 'Untitled';

              return (
                <Link
                  key={widget.id}
                  to={`/sites/${widget.site_id}/widgets/${widget.id}`}
                  className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          widget.type === 'quiz'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {widget.type}
                      </span>
                      <span className="text-xs text-gray-500">{widget.site_domain}</span>
                    </div>
                    <p className="font-medium text-gray-900 truncate">{title}</p>
                    <p className="text-sm text-gray-500 truncate">{widget.source_url}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(widget.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-gray-400 ml-4">&rarr;</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Sites Overview */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Active Sites</h2>
          <span className="text-sm text-gray-500">{sites.length} sites</span>
        </div>
        {loadingSites ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : sites.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No sites configured yet</p>
            <Link to="/sites" className="btn btn-primary">
              Add Your First Site
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sites.slice(0, 5).map((site) => (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{site.domain}</p>
                  <p className="text-sm text-gray-500">
                    Added {new Date(site.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-gray-400">&rarr;</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
  subtitle,
  period = 'Last 30 days',
}: {
  label: string;
  value?: number;
  loading: boolean;
  subtitle?: string;
  period?: string;
}) {
  return (
    <div className="card">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      {loading ? (
        <div className="h-8 mt-1 bg-gray-100 rounded animate-pulse" />
      ) : (
        <>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {(value ?? 0).toLocaleString()}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </>
      )}
      <p className="text-xs text-gray-400 mt-2">{period}</p>
    </div>
  );
}
